//  ==========================================================================
//
//  Who We Are
//
//  ==========================================================================
import React, { PureComponent } from 'react';

import { graphql } from 'gatsby';

import {
	Award,
	BlockquoteBreaker,
	CardFeature,
	CardFeatureGroup,
	GridStandard,
	HeroStandard,
	Image,
	MetaTags,
	Section,
	SectionFeature,
	SectionIntro,
	SectionWithCards,
	SectionWithHero,
} from '../components';

import { allRoutes, normalizeCuratedContent } from '../helpers';

import { css } from 'emotion';
import styled from 'react-emotion';
import { mq } from '../global-styles';

//  ==========================================================================
//  Styles
//  ==========================================================================
const StyledSectionIntro = styled( 'div' )`
	padding: 0 11px 11px;
	${mq.md(
		css`
			padding: 0 32px 20px;
		`
	)};
	${mq.lg(
		css`
			padding: 0 52px 30px;
		`
	)};
`;

//  ==========================================================================
//  Component
//  ==========================================================================
export default class WhoWeAre extends PureComponent {
	constructor( props ) {
		super( props );

		this._whoWeAreData = {
			description: props.data.pageMeta.who_we_are_meta_desc,
			hero: props.data.heroImage?.childImageSharp?.fluid,
			heroAlt:
				'Two workers walking down corridor in stylish concrete and wood office',
			jumpLinks: [
				{
					name: 'What We Believe',
					path: '#believe',
				},
				{
					name: 'Meet Our Leadership',
					path: '#leadership',
				},
				{
					name: 'Leading Voices',
					path: '#leading-voices',
				},
				{
					name: 'Building A Legacy',
					path: '#history',
				},
				{
					name: 'Awards',
					path: '#awards',
				},
			],
			whatWeBelieveImage:
				props.data.whatWeBelieveImage?.childImageSharp?.fluid,
			frameLogo: props.data.frameLogo?.childImageSharp?.fixed,
			architizerLogo: props.data.architizerLogo?.childImageSharp?.fixed,
			blockquoteImage:
				props.data.blockquoteBreakerImage?.childImageSharp?.fluid,
			leadingVoicesData: this.props.data?.articles?.nodes?.map(
				normalizeCuratedContent
			),
			imageBreak: props.data.imageBreak?.childImageSharp?.fluid,
			historyImage: props.data.historyImage?.childImageSharp?.fluid,
			jobsImage: props.data.jobsImage?.childImageSharp?.fluid,
			fellowshipsImage:
				props.data.fellowshipsImage?.childImageSharp?.fluid,
			awardFastCompany:
				props.data.awardFastCompany?.childImageSharp?.fluid,
			awardIIDA: props.data.awardIIDA?.childImageSharp?.fluid,
			awardHealthcareDesign: props.data.awardHealthcareDesign?.childImageSharp?.fluid,
			awardArchitecturalRecord:
				props.data.awardArchitecturalRecord?.childImageSharp?.fluid,
		};
	}

	render() {
		const {
			description,
			hero,
			heroAlt,
			jumpLinks,
			whatWeBelieveImage,
			blockquoteImage,
			leadingVoicesData,
			imageBreak,
			historyImage,
			jobsImage,
			fellowshipsImage,
			awardFastCompany,
			awardIIDA,
			awardHealthcareDesign,
			awardArchitecturalRecord,
			frameLogo,
			architizerLogo,
		} = this._whoWeAreData;

		return (
			<main id="main">
				<MetaTags post_title="Who We Are" description={description} />

				<HeroStandard
					hero={hero}
					heroAlt={heroAlt}
					label="Who We Are"
					post_title="Today, we’re building upon our legacy of industry-leading architectural delivery."
					description="We've become an interdisciplinary global design firm by expanding our team beyond architects and interior designers to include researchers, urban designers, nurses, anthropologists, graphic designers and more. At HKS, we view design as a process of discovery. Design research enables us to experiment, to improve — and to design smarter solutions. That’s particularly important as we strive to create a more resilient future."
					jumpLinks={jumpLinks}
					cta="Meet Our Team"
					link={allRoutes.people}
				/>

				<div className="content-contain">
					<Section post_title="believe">
						<SectionWithHero
							video="AIi6epG-4oM"
							image={whatWeBelieveImage}
							imageAlt="Three men talking in front of a large whiteboard covered with neighborhood layout plans, with workers at open-plan desks in the foreground"
							post_title="What We Believe"
							description="We believe that design should build trust between our collaborators, our clients and those who inhabit the places we design. We aim to inspire and empower the people and communities who encounter our work. To preserve and respect our natural resources, we create places that conserve and rejuvenate our planet, too."
							cta="What We Do"
							link={allRoutes.what_we_do}
						/>
					</Section>
				</div>

				<BlockquoteBreaker
					image={blockquoteImage}
					imageAlt="Headshot of Dan Noble"
					quote="Design is a way of thinking. It can uncover hidden opportunities and create meaningful change. Design done right delivers the extraordinary."
					name="Dan Noble"
					post_title={'President & CEO \nFAIA, FACHA, LEED AP'
						.split( '\n' )
						.map( ( item, i ) => (
							<p key={i}>{item}</p>
						) )}
					linkedin="dan-noble-8a2989b"
					path="/people/dan-noble"
				/>

				<div className="content-group content-group--no-btm-pad content-contain">
					<Section post_title="leadership" lastItem>
						<div className="content-contain__interior-lvl-one-to-two">
							<SectionIntro
								post_title="Our Leadership"
								description="We invite you to meet our team, led by a group of people respected for their personal character and professional expertise."
								cta="Meet Our Leaders"
								link="/leadership"
							/>
							<hr
								style={{
									maxWidth: '100%',
									width: 120,
									marginBottom: 0,
									paddingBottom: 0,
								}}
							/>
						</div>
					</Section>
				</div>

				{leadingVoicesData && 0 < leadingVoicesData.length && (
					<Section post_title="leading-voices">
						<div
							className="content-group content-contain"
							style={{ paddingTop: 0 }}
						>
							<SectionWithCards
								label="Explore Viewpoints"
								post_title="Leading Voices"
								cardContent={leadingVoicesData}
							/>
						</div>
					</Section>
				)}

				<div className="content-group content-group--no-top-pad content-contain">
					<Image
						alt="Four people sitting in armchairs around a coffee table with water glasses, discussing paperwork"
						sizes={imageBreak}
						style={{ position: 'relative' }}
						outerWrapperClassName="img-wrap"
						className="img"
					/>
				</div>

				<Section post_title="history" bgColorDark={true}>
					<div className="content-group content-contain">
						<SectionFeature
							image={historyImage}
							imageAlt="Black and white image of Howard K. Smith, HKS's founder, working on a design model on a table"
							label="History"
							post_title="Building a Legacy"
							description={[
								'Harwood K. Smith (left) was born and raised in Evanston, Illinois. A gifted painter, he studied at the world-renowned School of the Art Institute of Chicago. In college, Harwood opted to channel his artistic creativity into architecture. Upon graduation, he moved to Dallas, Texas, to begin an architectural practice.',
								'Harwood founded HKS in 1939. The Texas spirit—entrepreneurial, proud, resourceful & independent—underpins our culture to this day. Harwood’s artistic dreams also continue fuel our own: to inspire through design.',
							]}
						/>
					</div>
				</Section>

				<Section post_title="awards">
					<div className="content-contain overflow-hidden">
						<StyledSectionIntro>
							<SectionIntro
								post_title="Awards"
								description="For our ability to imagine, design and create environments that achieve meaningful impact, our firm has been honored with a number of international awards. Here is a selection of those we've won recently."
								cta="See More"
								link="/search?contentTypes=Awards"
							/>
						</StyledSectionIntro>

						<div className="content-contain__interior-lvl-one-to-two">
							<GridStandard customChildren={true}>
								<Award
									awardLogo={awardHealthcareDesign}
									awardAlt="Healthcare Design Awards logo"
									lineOne="2020 Outstanding Organization of the Year"
									lineTwo="for New Construction"
									cta="HKS"
									link="https://www.healthcaredesignmagazine.com/news/healthcare-design-names-2020-hcd-10-winners/"
								/>
								
								<Award
									awardLogo={awardFastCompany}
									awardAlt="2020 Fast Company Innovation by Design"
									lineOne="2020 Fast Company<br/>Innovation by Design"
									lineTwo="Spaces & Places, Health, and General Excellence"
									cta="COVID-19 Conversion Projects"
									link="/our-news/articles/hks-wins-three-2020-fast-company-innovation-by-design-awards-for-covid-19-conversion-projects/"
								/>
								<Award
									awardLogo={architizerLogo}
									awardAlt="2019 Architizer A+ logo"
									lineOne="2019 Architizer A+"
									lineTwo="Architecture for Greater Good"
									cta="Kachumbala Maternity Unit"
									link="/what-we-do/case-studies/kachumbala-maternity-unit/"
									imgStyle={{
										maxWidth: 130,
									}}
								/>
								
								<Award
									awardLogo={frameLogo}
									awardAlt="Frame architecture Logo"
									lineOne="2019 Frame"
									lineTwo="Social Award"
									cta="Kachumbala Maternity Unit"
									link="/what-we-do/case-studies/kachumbala-maternity-unit/"
									imgStyle={{
										maxWidth: 80,
									}}
								/>
								
								<Award
									awardLogo={awardArchitecturalRecord}
									awardAlt="Architectural Record logo"
									lineOne="2018 Innovator Women in Leadership Awards"
									lineTwo="Architectural Record Magazine"
									cta="Dr. Upali Nanda"
									link="/people/upali-nanda"
									imgStyle={{
										height: 50,
										marginTop: -20,
										transform: `translateY(10px)`,
									}}
								/>
								<Award
									awardLogo={awardIIDA}
									awardAlt="IIDA logo"
									lineOne="2018 IIDA Global Excellence"
									lineTwo="Corporate Large Space"
									cta="Elsley House"
									link="/what-we-do/case-studies/elsley-house/"
								/>
							</GridStandard>
						</div>
					</div>
				</Section>

				<Section
					post_title="crosslinks"
					bgColorDark={true}
					lastItem={true}
				>
					<div className="content-group content-contain">
						<CardFeatureGroup>
							<CardFeature
								image={jobsImage}
								imageAlt="Five people sitting around a table laughing and looking at a magazine"
								label="Careers"
								post_title="What's It Like to Work With Us?"
								cta="Join Our Team"
								link={allRoutes.join}
							/>
							<CardFeature
								image={fellowshipsImage}
								imageAlt="A man and a woman looking at designs laid out on a table in front of them"
								label="Fellowships"
								post_title="Encouraging Curiosity"
								cta="About Our Fellowships"
								link="/join-our-team#fellowships"
							/>
						</CardFeatureGroup>
					</div>
				</Section>
			</main>
		);
	}
}

export const whoWeAreQuery = graphql`
	query WhoWeAreQuery {
		articles: allWordsbyCollections(
			filter: {
				post_type: { eq: "article" }
				featured: { eq: true }
				sections: { in: "leading_voices" }
				post_title: { regex: "/^((?!Test Article).)*$/" }
			}
			sort: { fields: [post_date], order: DESC }
			limit: 6
		) {
			nodes {
				...CardComponent
			}
		}
		pageMeta: wordsbyCollections(post_type: { eq: "static_page_meta" }) {
			who_we_are_meta_desc
		}
		heroImage: file(relativePath: { regex: "/who-we-are--1-hero.jpg/" }) {
			childImageSharp {
				fluid(maxWidth: 2880) {
					...GatsbyImageSharpFluid_noBase64
				}
			}
		}
		whatWeBelieveImage: file(
			relativePath: { regex: "/who-we-are--2-video.jpg/" }
		) {
			childImageSharp {
				fluid(maxWidth: 2036) {
					...GatsbyImageSharpFluid_noBase64
				}
			}
		}
		blockquoteBreakerImage: file(
			relativePath: { regex: "/what-we-do--2-quote.jpg/" }
		) {
			childImageSharp {
				fluid(maxWidth: 2880) {
					...GatsbyImageSharpFluid_noBase64
				}
			}
		}
		imageBreak: file(
			relativePath: { regex: "/who-we-are--10-image-break.jpg/" }
		) {
			childImageSharp {
				fluid(maxWidth: 2036) {
					...GatsbyImageSharpFluid_noBase64
				}
			}
		}
		historyImage: file(
			relativePath: { regex: "/who-we-are--11-history.jpg/" }
		) {
			childImageSharp {
				fluid(maxWidth: 1138) {
					...GatsbyImageSharpFluid_noBase64
				}
			}
		}
		jobsImage: file(
			relativePath: { regex: "/shared-card-feature--jobs.jpg/" }
		) {
			childImageSharp {
				fluid(maxWidth: 2880) {
					...GatsbyImageSharpFluid_noBase64
				}
			}
		}
		fellowshipsImage: file(
			relativePath: { regex: "/shared-card-feature--fellowships.jpg/" }
		) {
			childImageSharp {
				fluid(maxWidth: 2880) {
					...GatsbyImageSharpFluid_noBase64
				}
			}
		}
		frameLogo: file(relativePath: { regex: "/frame-logo.png/" }) {
			childImageSharp {
				fixed(width: 80) {
					...GatsbyImageSharpFixed_noBase64
				}
			}
		}
		architizerLogo: file(
			relativePath: { regex: "/architizer-logo.png/" }
		) {
			childImageSharp {
				fixed(width: 130) {
					...GatsbyImageSharpFixed_noBase64
				}
			}
		}
		awardFastCompany: file(
			relativePath: { regex: "/awards/fast-company.png/" }
		) {
			childImageSharp {
				fluid(maxWidth: 106) {
					...GatsbyImageSharpFluid_noBase64
				}
			}
		}
		awardIIDA: file(relativePath: { regex: "/awards/iida.png/" }) {
			childImageSharp {
				fluid(maxWidth: 106) {
					...GatsbyImageSharpFluid_noBase64
				}
			}
		}
		awardArchitecturalRecord: file(
			relativePath: { regex: "/awards/architectural-record.png/" }
		) {
			childImageSharp {
				fluid(maxWidth: 120) {
					...GatsbyImageSharpFluid_noBase64
				}
			}
		}
		awardHealthcareDesign: file(
			relativePath: { regex: "/awards/healthcare-design-award.png/" }
		) {
			childImageSharp {
				fluid(maxWidth: 106) {
					...GatsbyImageSharpFluid_noBase64
				}
			}
		}
	}
`;
